import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Settings, BarChart3, Shield, ToggleLeft, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
} from '@/components/ui/OptimizedAnimations';

const cookieTypes = [
  {
    icon: Shield,
    title: 'Essential Cookies',
    description: 'Required for the platform to function. Cannot be disabled.',
    examples: ['Session management', 'Authentication', 'Security tokens', 'Load balancing'],
    required: true,
    color: 'teal',
  },
  {
    icon: Settings,
    title: 'Functional Cookies',
    description: 'Remember your preferences and settings.',
    examples: ['Language preferences', 'Theme settings', 'Dashboard layout', 'Form auto-fill'],
    required: false,
    color: 'orange',
  },
  {
    icon: BarChart3,
    title: 'Analytics Cookies',
    description: 'Help us understand how you use our platform.',
    examples: ['Page views', 'Feature usage', 'Error tracking', 'Performance metrics'],
    required: false,
    color: 'teal',
  },
];

const sections = [
  {
    title: 'What Are Cookies?',
    content: `Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and improve your experience.

We use cookies and similar technologies (like local storage) to provide, protect, and improve our services.`,
  },
  {
    title: 'How Long Do Cookies Last?',
    content: `• Session cookies: Deleted when you close your browser
• Persistent cookies: Remain for a set period (typically 1-12 months)
• Authentication cookies: Valid for your session or until you log out

You can view and delete cookies through your browser settings at any time.`,
  },
  {
    title: 'Third-Party Cookies',
    content: `We use limited third-party services that may set their own cookies:

• Payment processors (for secure transactions)
• Error monitoring (to fix issues quickly)
• Video hosting (for embedded tutorials)

We do not allow advertising cookies or sell data to third parties.`,
  },
  {
    title: 'Managing Your Preferences',
    content: `You can control cookies through:

• Browser settings: Block or delete all cookies
• Platform settings: Manage optional cookies in your account
• Opt-out links: Specific services provide opt-out mechanisms

Note: Blocking essential cookies may prevent you from using our services.`,
  },
];

const CookiesPage = () => {
  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient opacity-50" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal text-sm mb-8 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </ScrollReveal>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-teal/20 flex items-center justify-center">
              <Cookie className="w-7 h-7 text-teal" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                Cookie Policy
              </h1>
              <p className="text-muted-foreground text-sm">
                Last updated: July 2026
              </p>
            </div>
          </div>

          <ScrollReveal delay={0.3}>
            <p className="text-muted-foreground text-lg mb-8">
              This policy explains how Tattva Techworkz uses cookies and similar technologies
              to recognize you when you visit our platform.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Cookie Types */}
      <section className="py-12 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="mb-8">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-2">
              Types of Cookies We Use
            </h2>
            <p className="text-muted-foreground">
              We categorize cookies based on their purpose and necessity.
            </p>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-3 gap-6 mb-12">
            {cookieTypes.map((type) => (
              <StaggerItem key={type.title}>
                <InteractiveCard
                  hoverScale={1.02}
                  hoverY={-4}
                  glowColor={type.color === 'teal' ? 'rgba(0,191,165,0.2)' : 'rgba(255,107,53,0.2)'}
                >
                  <div className="glass-card p-6 h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className={cn(
                        'w-10 h-10 rounded-xl flex items-center justify-center',
                        type.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                      )}>
                        <type.icon className={cn(
                          'w-5 h-5',
                          type.color === 'teal' ? 'text-teal' : 'text-orange'
                        )} />
                      </div>
                      {type.required ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-teal/20 text-teal font-medium">
                          Required
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-glass-surface text-muted-foreground">
                          Optional
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-display font-semibold text-foreground mb-2">
                      {type.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {type.description}
                    </p>
                    <div className="space-y-1">
                      {type.examples.map((example) => (
                        <div key={example} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className={cn(
                            'w-1 h-1 rounded-full',
                            type.color === 'teal' ? 'bg-teal' : 'bg-orange'
                          )} />
                          {example}
                        </div>
                      ))}
                    </div>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* Additional Sections */}
          <StaggerContainer staggerDelay={0.1} className="space-y-6">
            {sections.map((section) => (
              <StaggerItem key={section.title}>
                <motion.div
                  whileHover={{ y: -2 }}
                  className="glass-card p-8"
                >
                  <h2 className="text-xl font-display font-semibold text-foreground mb-4">
                    {section.title}
                  </h2>
                  <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h3 className="text-display text-2xl text-foreground mb-4">
              Questions About Cookies?
            </h3>
            <p className="text-muted-foreground mb-6">
              If you have any questions about our use of cookies, please contact us.
            </p>
            <div className="inline-flex items-center gap-3 glass-card px-6 py-4">
              <Mail className="w-5 h-5 text-teal" />
              <a
                href="mailto:privacy@tattvatechworkz.com"
                className="text-teal hover:text-teal-light transition-colors"
              >
                privacy@tattvatechworkz.com
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default CookiesPage;
